'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { useT } from '@/components/i18n-provider';
import { useRouter } from 'next/navigation';
import { Send, ImagePlus, X, Loader2, Play } from 'lucide-react';
import { PostCard } from '@/components/post-card';
import { Spinner } from '@/components/ui/spinner';
import { MAX_POST_MEDIA } from '@/lib/posts/media';

const SORTS = [
  { id: 'hot', label: 'Hot' },
  { id: 'new', label: 'New' },
  { id: 'top', label: 'Top' },
] as const;
type Sort = (typeof SORTS)[number]['id'];

const PAGE = 20;

/**
 * The app/agent channel's post feed + discussion (channel page v2). Renders the
 * channel's posts through the platform's real {@link PostCard} (rich content,
 * media, embeds, likes/votes, repost, save, pin, quote, report, edit, delete),
 * sourced from `/api/posts?channel=<slug>` so it inherits the hot ranking,
 * pinned/featured handling and pagination. A composer posts into the channel.
 */
export function ChannelFeed({
  channelSlug,
  canPost,
  onOpenPost,
  onEdit,
}: {
  channelSlug: string;
  canPost: boolean;
  /**
   * In-panel drill-down for a post click. Absent → route to /p/[id]
   * (the standalone channel page). The apps-panel host passes this so a
   * click stays inside the panel's navigation stack.
   */
  onOpenPost?: (postId: string) => void;
  /**
   * In-panel edit for the card's "Edit" action. Absent → PostCard falls
   * back to the /p/[id]/edit route, which inside a PANEL navigated the
   * whole page out from under the overlay.
   */
  onEdit?: (postId: string) => void;
}) {
  const t = useT();
  const router = useRouter();
  const [posts, setPosts] = useState<Array<{ id: string; mediaUrl?: string; thumbnailUrl?: string }>>([]);
  const [sort, setSort] = useState<Sort>('hot');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [mediaItems, setMediaItems] = useState<Array<{ url: string; type: 'image' | 'video' }>>([]);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const attachMedia = async (files: File[]) => {
    setError(null);
    // Respect the gallery cap across this batch.
    let slots = MAX_POST_MEDIA - mediaItems.length;
    if (slots <= 0) { setError(`Up to ${MAX_POST_MEDIA} items per post.`); return; }
    setUploadingMedia(true);
    try {
      for (const file of files) {
        if (slots <= 0) { setError(`Up to ${MAX_POST_MEDIA} items per post.`); break; }
        const isImage = file.type.startsWith('image/');
        const isVideo = file.type.startsWith('video/');
        if (!isImage && !isVideo) { setError('Please choose image or video files.'); continue; }
        if (file.size > 20 * 1024 * 1024) { setError(`${isVideo ? t('workspace.publish.video') : t('workspace.publish.image')} must be under 20MB.`); continue; }
        const fd = new FormData();
        fd.append('file', file);
        fd.append('type', 'post');
        const res = await fetch('/api/upload', { method: 'POST', body: fd });
        const data = await res.json().catch(() => null);
        if (!res.ok || !data?.url) { setError(data?.error || `Upload failed (${res.status})`); continue; }
        slots -= 1;
        setMediaItems((prev) =>
          prev.length >= MAX_POST_MEDIA ? prev : [...prev, { url: data.url as string, type: isVideo ? 'video' : 'image' }],
        );
      }
    } finally {
      setUploadingMedia(false);
    }
  };

  const load = useCallback(
    async (offset: number, replace: boolean) => {
      if (replace) setLoading(true);
      else setLoadingMore(true);
      try {
        const res = await fetch(
          `/api/posts?channel=${encodeURIComponent(channelSlug)}&sort=${sort}&limit=${PAGE}&offset=${offset}`,
        );
        const data = res.ok ? await res.json() : null;
        const fresh = (data?.posts as typeof posts) ?? [];
        setHasMore(data?.hasMore === true && fresh.length > 0);
        setPosts((prev) => (replace ? fresh : [...prev, ...fresh]));
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [channelSlug, sort],
  );

  useEffect(() => { void load(0, true); }, [load]);

  const submit = async () => {
    if ((!content.trim() && mediaItems.length === 0) || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/channels/${channelSlug}/post`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          title: title.trim() || undefined,
          content: content.trim() || (mediaItems.length ? ' ' : ''),
          ...(mediaItems.length ? { media: mediaItems } : {}),
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => null))?.error || `Failed (${res.status})`);
      setTitle('');
      setContent('');
      setMediaItems([]);
      await load(0, true);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto mt-6 w-full max-w-2xl">
      <div className="mb-3 flex items-center justify-between px-1">
        <h2 className="text-sm font-semibold text-muted-foreground">Posts &amp; discussion</h2>
        <div className="flex gap-1">
          {SORTS.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setSort(s.id)}
              className={
                'rounded-md px-2 py-1 text-[12px] font-medium transition-colors ' +
                (sort === s.id ? 'bg-accent text-foreground' : 'text-muted-foreground hover:text-foreground')
              }
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {canPost ? (
        <div className="mb-5 rounded-xl border border-border/60 bg-background p-3">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Title (optional)"
            className="mb-2 w-full rounded-lg border border-border/50 bg-background px-3 py-2 text-sm outline-none focus:border-primary/60"
          />
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={2}
            placeholder="Share an update or start a discussion… (Markdown supported)"
            className="w-full resize-none rounded-lg border border-border/50 bg-background px-3 py-2 text-sm outline-none focus:border-primary/60"
          />
          {mediaItems.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {mediaItems.map((m, i) => (
                <div key={i} className="relative h-20 w-20 overflow-hidden rounded-lg border border-border/50">
                  {m.type === 'video' ? (
                    <video src={m.url} muted playsInline className="h-full w-full object-cover" />
                  ) : (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={m.url} alt="" className="h-full w-full object-cover" />
                  )}
                  {m.type === 'video' && (
                    <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-black/55">
                        <Play className="ml-0.5 h-3 w-3 fill-white text-white" />
                      </span>
                    </span>
                  )}
                  <button
                    type="button"
                    aria-label={t('workspace.publish.removeMedia')}
                    onClick={() => setMediaItems((prev) => prev.filter((_, j) => j !== i))}
                    className="absolute right-0.5 top-0.5 rounded-full bg-black/60 p-0.5 text-white hover:bg-black/80"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
          <div className="mt-2 flex items-center justify-between">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploadingMedia}
              aria-label={t('channelFeed.attachMedia')}
              className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-50"
            >
              {uploadingMedia ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
              Media
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*,video/*"
              multiple
              className="hidden"
              onChange={(e) => { const fs = Array.from(e.target.files ?? []); if (fs.length) void attachMedia(fs); e.target.value = ''; }}
            />
            <button
              type="button"
              onClick={() => void submit()}
              disabled={busy || (!content.trim() && mediaItems.length === 0)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-foreground px-4 py-1.5 text-sm font-medium text-background hover:opacity-90 disabled:opacity-50"
            >
              <Send className="h-3.5 w-3.5" />
              {busy ? t('workspace.publish.posting') : t('create.post')}
            </button>
          </div>
        </div>
      ) : (
        <p className="mb-5 rounded-xl border border-dashed border-border/60 px-3 py-3 text-xs text-muted-foreground">
          {t('channelFeed.signInToPost')}
        </p>
      )}

      {loading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : posts.length === 0 ? (
        <p className="px-1 py-6 text-center text-sm text-muted-foreground">{t('channelFeed.noPosts')}</p>
      ) : (
        <div className="flex flex-col gap-3">
          {posts.map((post, i) => (
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            <PostCard
              key={post.id}
              post={post as any}
              priority={i < 3}
              onOpenPost={(p: any) => {
                const id = p?.id ?? post.id;
                if (onOpenPost) onOpenPost(id);
                else router.push(`/p/${id}`);
              }}
              onEdit={onEdit}
            />
          ))}
          {hasMore && (
            <button
              type="button"
              onClick={() => void load(posts.length, false)}
              disabled={loadingMore}
              className="mx-auto mt-1 rounded-lg border border-border/60 px-4 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent disabled:opacity-50"
            >
              {loadingMore ? t('channelFeed.loading') : t('channelFeed.loadMore')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
